import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Upload, CheckCircle2, Loader2, AlertCircle, Clock } from "lucide-react";
import { api } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdUpload() {
  const { token } = useParams();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/ads/order/${token}`);
      setOrder(data);
      if (data.audio_uploaded) setDone(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Order not found.");
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(() => {
      // Poll while waiting for payment confirmation; stop once paid or uploaded.
      setOrder((cur) => {
        if (cur && (cur.payment_status === "paid" || cur.audio_uploaded)) {
          clearInterval(t);
          return cur;
        }
        refresh();
        return cur;
      });
    }, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onPick = () => fileRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setErr("");
    if (file.size > 25 * 1024 * 1024) { setErr("File too large. Max 25 MB."); return; }
    setUploading(true); setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${BACKEND_URL}/api/ads/upload/${token}`);
      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable) setProgress(Math.round((evt.loaded / evt.total) * 100));
      };
      xhr.onload = async () => {
        setUploading(false);
        if (xhr.status >= 200 && xhr.status < 300) {
          setDone(true);
          await refresh();
        } else {
          try {
            const r = JSON.parse(xhr.responseText || "{}");
            setErr(r.detail || "Upload failed. Try again.");
          } catch (_) {
            setErr("Upload failed. Try again.");
          }
        }
      };
      xhr.onerror = () => { setUploading(false); setErr("Network error during upload."); };
      xhr.send(fd);
    } catch (e) {
      setUploading(false);
      setErr("Upload failed.");
    }
  };

  if (!order) {
    return (
      <main className="max-w-xl mx-auto px-5 py-20 text-center">
        {err ? (
          <div className="text-red-400" data-testid="ad-upload-error">{err}</div>
        ) : (
          <div className="text-slate-500 fl-mono text-xs uppercase tracking-widest flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading your order…
          </div>
        )}
      </main>
    );
  }

  return (
    <main className="max-w-2xl mx-auto px-5 py-14" data-testid="ad-upload-page">
      <div className="fl-mono text-[11px] tracking-[0.3em] uppercase text-emerald-500">
        Order · {order.order_id}
      </div>
      <h1 className="fl-display text-4xl font-extrabold tracking-tighter mt-2">
        {order.business_name}
      </h1>
      <div className="text-slate-400 mt-1 text-sm">
        {order.package_id === "ad_5min" ? "5-minute slot" : "3-minute slot"} · ${order.amount} · {order.days} days
      </div>

      {/* Status panel */}
      <div className="fl-card p-5 mt-8" data-testid="ad-upload-status">
        {order.payment_status !== "paid" ? (
          <div className="flex items-start gap-3">
            <Loader2 className="w-5 h-5 text-emerald-500 animate-spin shrink-0 mt-0.5" />
            <div>
              <div className="text-white font-medium">Waiting for payment confirmation…</div>
              <div className="text-xs text-slate-400 mt-1">
                Stripe usually confirms in a few seconds. This page refreshes automatically.
                If you closed the Stripe tab without paying, <a href="/advertise" className="text-emerald-500 hover:underline">go back to start over</a>.
              </div>
            </div>
          </div>
        ) : done ? (
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-400 shrink-0 mt-0.5" />
            <div>
              <div className="text-white font-medium">Audio received — pending review</div>
              <div className="text-xs text-slate-400 mt-1">
                We'll review your ad within 24 hours. You'll get an email when it's live.
                <br />Watch this page or your inbox for updates.
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start gap-3 mb-5">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
              <div>
                <div className="text-white font-medium">Payment received</div>
                <div className="text-xs text-slate-400 mt-1">
                  Upload your audio file below. Max {order.max_duration}s, up to 25 MB. MP3 preferred.
                </div>
              </div>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg,audio/mp4,audio/aac"
              className="hidden"
              onChange={onFile}
              data-testid="ad-upload-input"
            />
            {uploading ? (
              <div data-testid="ad-upload-progress">
                <div className="text-sm text-slate-300">Uploading… {progress}%</div>
                <div className="mt-2 h-2 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
            ) : (
              <button onClick={onPick} className="fl-btn" data-testid="ad-upload-button">
                <Upload className="w-4 h-4" /> Choose audio file
              </button>
            )}
          </>
        )}
        {err && (
          <div className="mt-4 flex items-start gap-2 text-sm text-red-400" data-testid="ad-upload-err">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {err}
          </div>
        )}
      </div>

      <div className="text-xs text-slate-500 mt-4 flex items-center gap-2">
        <Clock className="w-3.5 h-3.5" /> Most ads go live within a few hours of upload.
      </div>
    </main>
  );
}
